import { cn } from '@repo/tailwind-config/tailwind.functions'
import { SVGFC } from '@repo/toolkit/types'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import { Link } from 'react-router-dom'

import useSidebarManager from '../../../lib/hooks/use-sidebar-manager'
import { SidebarItemKey } from '../../layouts/main-layout'
import SidebarToggleItem from './sidebar-toggle-item'

export type SidebarItemType = {
  itemKey: SidebarItemKey
  text: string
  icon?: SVGFC
  isIconFilled?: boolean
  url?: string
  hasAccess?: boolean
  items?: SidebarItemType[]
  activeUrls?: string[]
}

type SidebarProp = {
  itemKey: SidebarItemKey
  variant: 'thick-padding' | 'thin-padding'
}

const SidebarItem = (item: SidebarItemType & SidebarProp) => {
  const { itemKey, url, items, variant } = item
  const device = useBreakPoint()
  const isMobile = device === 'mobile'

  const {
    isSidebarExpanded,
    isItemActive,
    isItemExpanded,
    handleItemClick,
    handleSidebarExpansion
  } = useSidebarManager()

  const isExpanded = isItemExpanded(itemKey)
  const isActive = isItemActive(itemKey)
  const Tag = url ? Link : 'div' // Decide whether to use Link or div

  return (
    <div
      onClick={() => {
        if (!isSidebarExpanded) {
          handleSidebarExpansion(true)
          handleItemClick(itemKey)
        }
      }}
      className={cn(' hover:bg-white/15 transition-all duration-300 ease-in-out py-1', {
        'bg-white/15': isActive,
        'rounded-lg': variant === 'thick-padding',
        'rounded-sm': variant === 'thin-padding'
      })}
    >
      <Tag
        onClick={() => handleItemClick(itemKey, !!url && isMobile)}
        to={url || ''}
        className={cn('flex items-center justify-between  cursor-pointer', {
          'justify-center': !isSidebarExpanded,
          'px-2 py-1.5': variant === 'thin-padding',
          'py-3 px-4': variant === 'thick-padding'
        })}
      >
        <SidebarToggleItem
          mode={isSidebarExpanded ? 'expanded' : 'collapsed'}
          item={item}
          isActive={isActive}
          isExpanded={isExpanded}
          variant={variant}
        />
      </Tag>

      {isSidebarExpanded && items && items.length > 0 && (
        <div
          className={cn('overflow-hidden transition-all duration-300 mx-2 flex flex-col gap-y-1', {
            'max-h-0': !isExpanded,
            'max-h-screen': isExpanded
          })}
        >
          {items
            ?.filter((item) => item.hasAccess !== false)
            ?.map((item) => (
              <SidebarItem key={item.itemKey} {...item} variant="thin-padding" />
            ))}
        </div>
      )}
    </div>
  )
}

export default SidebarItem
